/**
 * Billing Effects
 * NgRx effects for billing side effects and API integration
 */
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { BillingService } from '../services/billing.service';
import { NotificationService } from '@core/services/notification.service';
import * as BillingActions from './billing.actions';

@Injectable()
export class BillingEffects {
  private actions$ = inject(Actions);
  private billingService = inject(BillingService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  loadPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadPlans),
      switchMap(() =>
        this.billingService.getPricingPlans().pipe(
          map((response) => BillingActions.loadPlansSuccess({ plans: response.plans })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load pricing plans';
            this.notificationService.showError(message);
            return of(BillingActions.loadPlansFailure({ error: message }));
          })
        )
      )
    )
  );

  loadSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadSubscription),
      switchMap(() =>
        this.billingService.getSubscription().pipe(
          map((subscription) => BillingActions.loadSubscriptionSuccess({ subscription })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load subscription';
            this.notificationService.showError(message);
            return of(BillingActions.loadSubscriptionFailure({ error: message }));
          })
        )
      )
    )
  );

  loadUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.loadUsage),
      switchMap(() =>
        this.billingService.getUsage().pipe(
          map((usage) => BillingActions.loadUsageSuccess({ usage })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load usage statistics';
            this.notificationService.showError(message);
            return of(BillingActions.loadUsageFailure({ error: message }));
          })
        )
      )
    )
  );

  createSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.createSubscription),
      switchMap(({ request }) =>
        this.billingService.createSubscription(request).pipe(
          map((response) => {
            this.notificationService.showSuccess('Subscription created successfully!');
            return BillingActions.createSubscriptionSuccess({ response });
          }),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to create subscription';
            this.notificationService.showError(message);
            return of(BillingActions.createSubscriptionFailure({ error: message }));
          })
        )
      )
    )
  );

  updateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.updateSubscription),
      switchMap(({ request }) =>
        this.billingService.updateSubscription(request).pipe(
          map((subscription) => {
            this.notificationService.showSuccess('Subscription updated successfully!');
            return BillingActions.updateSubscriptionSuccess({ subscription });
          }),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to update subscription';
            this.notificationService.showError(message);
            return of(BillingActions.updateSubscriptionFailure({ error: message }));
          })
        )
      )
    )
  );

  cancelSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.cancelSubscription),
      switchMap(({ immediately }) =>
        this.billingService.cancelSubscription(immediately).pipe(
          map((response) => {
            const message = immediately
              ? 'Subscription canceled immediately'
              : 'Subscription will be canceled at the end of the billing period';
            this.notificationService.showSuccess(message);
            return BillingActions.cancelSubscriptionSuccess({ response });
          }),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to cancel subscription';
            this.notificationService.showError(message);
            return of(BillingActions.cancelSubscriptionFailure({ error: message }));
          })
        )
      )
    )
  );

  reactivateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.reactivateSubscription),
      switchMap(() =>
        this.billingService.reactivateSubscription().pipe(
          map((subscription) => {
            this.notificationService.showSuccess('Subscription reactivated successfully!');
            return BillingActions.reactivateSubscriptionSuccess({ subscription });
          }),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to reactivate subscription';
            this.notificationService.showError(message);
            return of(BillingActions.reactivateSubscriptionFailure({ error: message }));
          })
        )
      )
    )
  );

  redirectToCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.redirectToCheckout),
      switchMap(({ stripePriceId, trialDays }) =>
        this.billingService.redirectToCheckout(stripePriceId, trialDays).pipe(
          map(() => BillingActions.loadSubscription()),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to redirect to checkout';
            this.notificationService.showError(message);
            return of(BillingActions.redirectToCheckoutFailure({ error: message }));
          })
        )
      )
    )
  );

  redirectToCustomerPortal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingActions.redirectToCustomerPortal),
      switchMap(() =>
        this.billingService.redirectToCustomerPortal().pipe(
          map(() => BillingActions.loadSubscription()),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to redirect to customer portal';
            this.notificationService.showError(message);
            return of(BillingActions.redirectToCustomerPortalFailure({ error: message }));
          })
        )
      )
    )
  );

  // Reload usage after subscription changes
  reloadUsageAfterSubscriptionChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BillingActions.createSubscriptionSuccess,
        BillingActions.updateSubscriptionSuccess,
        BillingActions.cancelSubscriptionSuccess,
        BillingActions.reactivateSubscriptionSuccess
      ),
      map(() => BillingActions.loadUsage())
    )
  );
}
